/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { IForm, ComponentBase } from 'app/shared/lib/essette-site';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { bindable } from 'aurelia-typed-observable-plugin';
import { TaskService } from 'app/core/services/task-service';
import { apiCall } from 'app/shared/lib/api/api';
import { Toaster } from 'app/shared/lib/toaster';
import { deepClone } from 'app/shared/lib/utilities';
import { ITaskAppointmentsConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import { DateTimeValue, DateValue } from 'app/shared/lib/date-time';
import * as Core from 'app/core/models/essette-api';


export interface ISaveTaskAppointmentChanges {
    appointment: Core.TaskAppointment;
}

@autoinject
export class SaveTaskAppointmentCustomElement extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull
    protected appointment: Core.TaskAppointment | null = null;

    @bindable.objectNull
    protected config!: ITasksConfig;

    @bindable.boolean
    protected showActions: boolean = false;

    private appointmentConfig: ITaskAppointmentsConfig | null = null;
    protected addAppointment: boolean = true;
    protected startDate: string | null = null;
    protected startTime: string | null = null;
    protected endTime: string | null = null;

    public submittedChanges: ISaveTaskAppointmentChanges | null = null;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: TaskService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!(this.config && !!this.config.appointmentsConfig && !!this.config.appointmentsConfig.addTargetProperty)) {
            throw new Error('Bindable property [config] was not set');
        }

        this.appointmentConfig = this.config.appointmentsConfig;
        this.service.configure(this.config);
    }


    ////****************** Events ***************************************************////
    protected appointmentChanged(): void {
        if(!this.appointment) {
            throw new Error('Bindable property [appointment] was not set');
        }
        if(!this.appointment.dateTimeRange) {
            this.appointment.dateTimeRange = {
                start: null,
                end: null
            }
        }

        const start = new DateTimeValue(this.appointment.dateTimeRange!.start!).toDate(this.env.timeZone);
        this.startDate = start.toJSON();
        this.startTime = this.appointment.dateTimeRange.start || null;
        this.endTime = this.appointment.dateTimeRange.end || null;

        this.addAppointment = !this.appointment.id;
    }


    ////****************** Functions ************************************************////
    private validate(appointment: Core.TaskAppointment): boolean {
        return !!(appointment &&
            appointment.text &&
            appointment.dateTimeRange &&
            appointment.dateTimeRange.start &&
            appointment.dateTimeRange.end);
    }

    private constructSaveObject(): Core.TaskAppointment {
        if(!this.appointment) {
            throw new Error('Bindable property "appointment" not provided');
        }

        const appointment = {
            id: this.appointment.id || undefined,
            text: this.appointment.text,
            dateTimeRange: {
                start: this.adjustDate(this.startTime),
                end: this.adjustDate(this.endTime)
            }
        };

        if(this.appointmentConfig && this.appointmentConfig.addTargetProperty) {
            this.appointmentConfig.addTargetProperty(appointment);
        }

        return appointment;
    }

    //TODO: Date adjustment should happen in ess-time.  Without this, if you edit an appointment and only change the time values, the date is set to today's date
    private adjustDate(value: string | null): string | null {
        if(!value) { return null; }

        if(this.startDate) {
            return new DateTimeValue(value).changeDate(new DateValue(this.startDate), this.env.displayTimeZone).toJSON();
        }
        else { return null; }
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }
    public async submit(): Promise<boolean> {
        this.submittedChanges = null;

        const saveAppointment = this.constructSaveObject();
        if(this.validate(saveAppointment)) {
            return await this.saveAppointment(saveAppointment!);
        }
        else {
            return false;
        }
    }
    public reset(): void {
        this.startDate = null;
        this.startTime = null;
        this.endTime = null;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveAppointment(appointment: Core.TaskAppointment): Promise<boolean> {
        try {
            this.state.beginSave();
            const response = await this.service.saveTaskAppointment(appointment);
            this.toaster.standard.saved('core:task.taskAppointment.$', response.isSuccessful);
            if(response.isSuccessful) {
                this.submittedChanges = { appointment: deepClone(appointment) };
                this.reset();
                return true;
            }
            else {
                this.logger.error(response.resultText!);
                return false;
            }
        } catch(error) {
            this.toaster.standard.saved('core:task.taskAppointment.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}
